import React, { useState, useEffect } from 'react';
import { Clock, PlusCircle, Timer, Search, Zap, ShieldCheck, Check, Send, Edit, Trash2, AlertTriangle, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import { SERVICE_PLUS_ITEMS } from './constants';

export const LabourSection = ({
  woId, workOrder, canEditPricing,
  timeEntries, allocatedHours, setAllocatedHours,
  recherchePanne, setRecherchePanne,
  diagnosticObd, setDiagnosticObd,
  servicePlus, setServicePlus,
  employees, onTimeEntriesChanged,
}) => {
  const [editingTimeEntry, setEditingTimeEntry] = useState(null);
  const [editDuration, setEditDuration] = useState('');
  const [rpDuration, setRpDuration] = useState('1');
  const [obdMinutes, setObdMinutes] = useState('30');
  const [spItems, setSpItems] = useState(SERVICE_PLUS_ITEMS);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualEmployee, setManualEmployee] = useState('');
  const [manualHours, setManualHours] = useState('');
  const [manualMinutes, setManualMinutes] = useState('');
  const [manualDate, setManualDate] = useState('');
  const [savingManual, setSavingManual] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/service-plus-tariffs`)
      .then(r => { if (r.data.tariffs?.length) setSpItems(r.data.tariffs); })
      .catch(() => {});
  }, []);

  const saveAllocatedHours = async (val) => {
    setAllocatedHours(val);
    const hours = parseFloat(val);
    if (isNaN(hours) || hours < 0) return;
    try {
      await axios.put(`${API}/work-orders/${woId}`, { allocated_hours: hours });
    } catch { /* auto-save */ }
  };

  const deleteTimeEntry = async (entryId) => {
    if (!window.confirm('Supprimer cette entree de temps ?')) return;
    try {
      await axios.delete(`${API}/work-orders/${woId}/time-entries/${entryId}`);
      toast.success('Entree supprimee');
      onTimeEntriesChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const saveTimeEntry = async () => {
    if (!editingTimeEntry) return;
    const mins = parseInt(editDuration);
    if (isNaN(mins) || mins < 0) { toast.error('Duree invalide'); return; }
    try {
      await axios.put(`${API}/work-orders/${woId}/time-entries/${editingTimeEntry.id}`, { duration_minutes: mins });
      toast.success('Heures modifiees');
      setEditingTimeEntry(null);
      onTimeEntriesChanged();
    } catch { toast.error('Erreur'); }
  };

  const updateWorkType = async (newType) => {
    try {
      await axios.put(`${API}/work-orders/${woId}`, { work_type: newType });
      onTimeEntriesChanged();
      toast.success('Type de travail mis a jour');
    } catch { toast.error('Erreur'); }
  };

  const saveRecherchePanne = async (notify = false) => {
    const durationHours = parseFloat(rpDuration);
    if (isNaN(durationHours) || durationHours <= 0) { toast.error('Duree invalide'); return; }
    try {
      const res = await axios.post(`${API}/work-orders/${woId}/recherche-panne`, {
        duration_hours: durationHours, notify_client: notify,
      });
      setRecherchePanne(res.data.recherche_panne || res.data);
      if (res.data.whatsapp_sent) {
        toast.success('Recherche de pannes enregistrée - WhatsApp envoyé au client');
      } else if (res.data.whatsapp_error) {
        toast.error('Token WhatsApp expiré — Renouvelez-le dans Meta Business Manager');
        toast.success('Recherche de pannes enregistrée');
      } else if (res.data.notified) {
        toast.success('Recherche de pannes enregistrée - Client notifié');
      } else {
        toast.success('Recherche de pannes enregistrée');
      }
      onTimeEntriesChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const removeRecherchePanne = async () => {
    try {
      await axios.put(`${API}/work-orders/${woId}`, { recherche_panne: null });
      setRecherchePanne(null);
      toast.success('Recherche de pannes supprimee');
      onTimeEntriesChanged();
    } catch { toast.error('Erreur'); }
  };

  const saveDiagnosticObd = async () => {
    const mins = parseInt(obdMinutes);
    if (isNaN(mins) || mins <= 0) { toast.error('Duree invalide'); return; }
    try {
      const res = await axios.post(`${API}/work-orders/${woId}/diagnostic-obd`, { duration_minutes: mins });
      setDiagnosticObd(res.data);
      toast.success(`Diagnostic OBD enregistre: ${res.data.cost_ht} CHF HT`);
      onTimeEntriesChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const removeDiagnosticObd = async () => {
    try {
      await axios.put(`${API}/work-orders/${woId}`, { diagnostic_obd: null });
      setDiagnosticObd(null);
      toast.success('Diagnostic OBD supprime');
      onTimeEntriesChanged();
    } catch { toast.error('Erreur'); }
  };

  const calcObdPrice = (mins) => {
    const m = parseInt(mins) || 30;
    if (m <= 30) return 75;
    return 75 + Math.ceil((m - 30) / 10) * 10;
  };

  // Normalize servicePlus to array format (handles both array and object formats)
  const normalizedServicePlus = Array.isArray(servicePlus) 
    ? servicePlus 
    : (servicePlus && typeof servicePlus === 'object' 
        ? Object.keys(servicePlus).filter(k => servicePlus[k]) 
        : []);

  const toggleServicePlus = async (serviceId) => {
    const updated = normalizedServicePlus.includes(serviceId)
      ? normalizedServicePlus.filter(s => s !== serviceId)
      : [...normalizedServicePlus, serviceId];
    setServicePlus(updated);
    try {
      await axios.put(`${API}/work-orders/${woId}`, { service_plus: updated });
    } catch { /* auto-save */ }
  };

  const servicePlusTotal = normalizedServicePlus.reduce((sum, id) => {
    const item = spItems.find(s => s.id === id);
    return sum + (item ? item.price : 0);
  }, 0);

  const atelierEmployees = employees.filter(e => e.role === 'atelier' || e.role === 'employe');

  const saveManualEntry = async () => {
    if (!manualEmployee) { toast.error('Selectionnez un mecanicien'); return; }
    const h = parseInt(manualHours) || 0;
    const m = parseInt(manualMinutes) || 0;
    const totalMinutes = h * 60 + m;
    if (totalMinutes <= 0) { toast.error('Duree invalide'); return; }
    const emp = employees.find(e => e.id === manualEmployee);
    const empName = emp ? `${emp.first_name} ${emp.last_name}`.trim() : 'Manuel';
    setSavingManual(true);
    try {
      await axios.post(`${API}/work-orders/${woId}/time-entries`, {
        employee_id: manualEmployee,
        employee_name: empName,
        duration_minutes: totalMinutes,
        date: manualDate || undefined,
      });
      toast.success(`${empName} — ${h}h${String(m).padStart(2, '0')} ajoute`);
      setShowManualForm(false);
      setManualEmployee('');
      setManualHours('');
      setManualMinutes('');
      setManualDate('');
      onTimeEntriesChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSavingManual(false); }
  };

  return (
    <Card className="p-4 rounded-xl space-y-4" data-testid="wo-labour-section">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <Clock className="w-4 h-4" /> Main d'oeuvre
          {canEditPricing && timeEntries && timeEntries.total_cost_ht > 0 && (
            <span className="text-blue-600 font-bold ml-2">{timeEntries.total_cost_ht.toFixed(2)} CHF HT</span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          {canEditPricing && (
            <Button
              variant={showManualForm ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setShowManualForm(!showManualForm)}
              data-testid="add-manual-time-btn"
            >
              <PlusCircle className="w-3.5 h-3.5 mr-1" />
              Saisie manuelle
            </Button>
          )}
        </div>
      </div>

      {/* Manual time entry form */}
      {showManualForm && (
        <div className="border-2 border-amber-200 bg-amber-50 rounded-lg p-4 space-y-3" data-testid="manual-time-form">
          <p className="text-xs font-bold text-amber-700 uppercase tracking-wider flex items-center gap-1.5">
            <UserPlus className="w-3.5 h-3.5" /> Ajouter du temps manuellement
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Mecanicien *</Label>
              <Select value={manualEmployee} onValueChange={setManualEmployee}>
                <SelectTrigger className="h-9 bg-white" data-testid="manual-employee-select">
                  <SelectValue placeholder="Choisir un mecanicien" />
                </SelectTrigger>
                <SelectContent>
                  {atelierEmployees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Date (optionnel)</Label>
              <Input
                type="date"
                value={manualDate}
                onChange={(e) => setManualDate(e.target.value)}
                className="h-9 bg-white"
                data-testid="manual-date-input"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Duree *</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="0"
                max="24"
                value={manualHours}
                onChange={(e) => setManualHours(e.target.value)}
                placeholder="0"
                className="h-9 w-20 bg-white text-center"
                data-testid="manual-hours-input"
              />
              <span className="text-sm text-slate-500 font-medium">h</span>
              <Input
                type="number"
                min="0"
                max="59"
                step="5"
                value={manualMinutes}
                onChange={(e) => setManualMinutes(e.target.value)}
                placeholder="00"
                className="h-9 w-20 bg-white text-center"
                data-testid="manual-minutes-input"
              />
              <span className="text-sm text-slate-500 font-medium">min</span>
              {(parseInt(manualHours) > 0 || parseInt(manualMinutes) > 0) && timeEntries && (
                <span className="text-xs text-blue-600 font-medium ml-2">
                  = {(((parseInt(manualHours) || 0) + (parseInt(manualMinutes) || 0) / 60) * timeEntries.hourly_rate).toFixed(2)} CHF
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button size="sm" onClick={saveManualEntry} disabled={savingManual} data-testid="save-manual-time-btn">
              <Check className="w-3.5 h-3.5 mr-1" />
              {savingManual ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowManualForm(false)} data-testid="cancel-manual-time-btn">
              Annuler
            </Button>
          </div>
        </div>
      )}

      {/* Work type selector */}
      {canEditPricing && timeEntries && (
        <div className="flex gap-1.5 flex-wrap">
          {timeEntries.available_types.map(t => (
            <button key={t.id} type="button" onClick={() => updateWorkType(t.id)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                timeEntries.work_type === t.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'
              }`} data-testid={`work-type-${t.id}`}>
              {t.label} ({t.rate} CHF/h)
            </button>
          ))}
        </div>
      )}

      {/* Temps alloue */}
      {canEditPricing && workOrder.status !== 'completed' && (
        <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="allocated-hours-section">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
              <Timer className="w-3.5 h-3.5" /> Temps alloue au mecanicien
            </Label>
            {allocatedHours && timeEntries && timeEntries.total_hours > parseFloat(allocatedHours) && (
              <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full flex items-center gap-1 animate-pulse" data-testid="overtime-alert">
                <AlertTriangle className="w-3 h-3" /> DEPASSEMENT
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Input type="number" step="0.5" min="0" max="100" value={allocatedHours} onChange={(e) => saveAllocatedHours(e.target.value)} placeholder="Ex: 3"
              className={`h-9 w-32 text-sm font-medium ${allocatedHours && timeEntries && timeEntries.total_hours > parseFloat(allocatedHours) ? 'border-red-400 bg-red-50 text-red-700' : ''}`}
              data-testid="allocated-hours-input" />
            <span className="text-xs text-slate-500">heures prevues</span>
            {allocatedHours && timeEntries && (
              <div className={`text-xs font-medium px-2 py-1 rounded ${
                timeEntries.total_hours > parseFloat(allocatedHours) ? 'bg-red-100 text-red-700 border border-red-300' :
                timeEntries.total_hours > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
              }`} data-testid="time-progress">
                {timeEntries.total_hours}h / {parseFloat(allocatedHours)}h
                {timeEntries.total_hours > parseFloat(allocatedHours) && (
                  <span className="ml-1 font-bold">(+{(timeEntries.total_hours - parseFloat(allocatedHours)).toFixed(1)}h)</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {canEditPricing && workOrder.status === 'completed' && allocatedHours && (
        <div className={`text-xs px-3 py-2 rounded-lg flex items-center gap-2 ${
          timeEntries && timeEntries.total_hours > parseFloat(allocatedHours) ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-slate-50 text-slate-600'
        }`} data-testid="allocated-hours-display">
          <Timer className="w-3.5 h-3.5" />
          Temps alloue: {allocatedHours}h | Realise: {timeEntries?.total_hours || 0}h
          {timeEntries && timeEntries.total_hours > parseFloat(allocatedHours) && (
            <span className="font-bold text-red-600 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> DEPASSEMENT +{(timeEntries.total_hours - parseFloat(allocatedHours)).toFixed(1)}h
            </span>
          )}
        </div>
      )}

      {/* Recherche de pannes */}
      {canEditPricing && (
        <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="recherche-panne-section">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
              <Search className="w-3.5 h-3.5" /> Recherche de pannes
              <span className="text-blue-600 font-normal normal-case">(100 CHF/h HT)</span>
            </Label>
            {recherchePanne?.active && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">Active</span>}
          </div>
          {recherchePanne?.active ? (
            <div className="flex items-center justify-between bg-emerald-50 rounded-lg px-3 py-2 text-sm" data-testid="rp-active-display">
              <div>
                <span className="font-medium text-slate-900">Duree: {recherchePanne.duration_hours}h</span>
                <span className="text-emerald-700 font-bold ml-3">{recherchePanne.cost_ht?.toFixed(2)} CHF HT</span>
              </div>
              {workOrder.status !== 'completed' && (
                <button type="button" onClick={removeRecherchePanne} className="text-red-400 hover:text-red-600" data-testid="remove-rp-btn"><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </div>
          ) : workOrder.status !== 'completed' ? (
            <div className="space-y-2">
              <p className="text-xs text-slate-500">Duree demandee par le client :</p>
              <div className="flex items-center gap-2">
                {['1', '1.5', '2'].map(d => (
                  <button key={d} type="button" onClick={() => setRpDuration(d)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${rpDuration === d ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}
                    data-testid={`rp-duration-${d}`}>
                    {d === '1.5' ? '1h30' : `${d}h`}
                  </button>
                ))}
                <Input type="number" step="0.5" min="0.5" max="8" value={rpDuration} onChange={(e) => setRpDuration(e.target.value)} className="h-9 w-20 text-sm" data-testid="rp-duration-custom" />
                <span className="text-xs text-slate-400">= {(parseFloat(rpDuration) * 100).toFixed(0)} CHF HT</span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => saveRecherchePanne(false)} data-testid="save-rp-btn"><Check className="w-3.5 h-3.5 mr-1" /> Enregistrer</Button>
                <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200" onClick={() => saveRecherchePanne(true)} data-testid="save-rp-notify-btn">
                  <Send className="w-3.5 h-3.5 mr-1" /> Enregistrer & Confirmer au client
                </Button>
              </div>
            </div>
          ) : <p className="text-xs text-slate-400">Non demandee</p>}
        </div>
      )}

      {/* Diagnostic OBD */}
      {canEditPricing && (
        <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="diagnostic-obd-section">
          <Label className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5" /> Diagnostic OBD
            <span className="text-blue-600 font-normal normal-case">(75 CHF HT / 30 min, +10 CHF / 10 min)</span>
          </Label>
          {diagnosticObd?.active ? (
            <div className="flex items-center justify-between bg-emerald-50 rounded-lg px-3 py-2 text-sm" data-testid="obd-active-display">
              <div>
                <span className="font-medium text-slate-900">{diagnosticObd.duration_minutes} min</span>
                <span className="text-emerald-700 font-bold ml-3">{diagnosticObd.cost_ht?.toFixed(2)} CHF HT</span>
              </div>
              {workOrder.status !== 'completed' && (
                <button type="button" onClick={removeDiagnosticObd} className="text-red-400 hover:text-red-600" data-testid="remove-obd-btn"><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </div>
          ) : workOrder.status !== 'completed' ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {[30, 40, 50, 60].map(m => (
                  <button key={m} type="button" onClick={() => setObdMinutes(String(m))}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${obdMinutes === String(m) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}
                    data-testid={`obd-duration-${m}`}>{m} min</button>
                ))}
                <Input type="number" step="10" min="10" max="180" value={obdMinutes} onChange={(e) => setObdMinutes(e.target.value)} className="h-9 w-20 text-sm" data-testid="obd-duration-custom" />
                <span className="text-xs text-slate-400 font-medium">= {calcObdPrice(obdMinutes)} CHF HT</span>
              </div>
              <Button size="sm" onClick={saveDiagnosticObd} data-testid="save-obd-btn"><Zap className="w-3.5 h-3.5 mr-1" /> Enregistrer diagnostic OBD</Button>
            </div>
          ) : <p className="text-xs text-slate-400">Non demande</p>}
        </div>
      )}

      {/* Service Plus */}
      {canEditPricing && (
        <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="service-plus-section">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Service Plus
              <span className="text-blue-600 font-normal normal-case">(forfaits HT)</span>
            </Label>
            {servicePlusTotal > 0 && (
              <span className="text-sm font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full" data-testid="service-plus-total">{servicePlusTotal} CHF HT</span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {spItems.map(item => {
              const isActive = normalizedServicePlus.includes(item.id);
              return (
                <button key={item.id} type="button" onClick={() => workOrder.status !== 'completed' && toggleServicePlus(item.id)}
                  className={`flex items-center justify-between text-left px-3 py-2 rounded-lg text-sm border transition-all ${
                    isActive ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300'
                  } ${workOrder.status === 'completed' ? 'cursor-default' : 'cursor-pointer'}`}
                  data-testid={`sp-${item.id}`}>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300'}`}>
                      {isActive && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="min-w-0">
                      <span className={`font-medium ${isActive ? 'text-emerald-800' : 'text-slate-700'}`}>{item.label}</span>
                      {item.description && <p className="text-[10px] text-slate-400 truncate">{item.description}</p>}
                    </div>
                  </div>
                  <span className={`font-bold flex-shrink-0 ml-2 ${isActive ? 'text-emerald-700' : 'text-slate-500'}`}>{item.price}.-</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Time entries list */}
      {timeEntries && timeEntries.entries.length > 0 ? (
        <div className="space-y-1.5">
          {timeEntries.entries.map((e, i) => (
            <div key={e.id || i} className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${e.status === 'active' ? 'bg-green-50 border border-green-200 animate-pulse' : e.is_manual ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`} data-testid={`time-entry-${i}`}>
              <div className="flex-1 flex items-center gap-2">
                <span className="font-medium text-slate-900">{e.employee_name}</span>
                {e.status === 'active' && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">En cours</span>}
                {e.is_manual && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Manuel</span>}
                {e.source === 'atelier' && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">Atelier</span>}
                {e.date && <span className="text-xs text-slate-400">{e.date}</span>}
              </div>
              <div className="flex items-center gap-3">
                {!e.is_manual && (
                  <span className="text-xs text-slate-500">
                    {e.start_time ? new Date(e.start_time).toLocaleTimeString('fr-CH', {hour:'2-digit', minute:'2-digit'}) : '-'}
                    {' - '}
                    {e.end_time ? new Date(e.end_time).toLocaleTimeString('fr-CH', {hour:'2-digit', minute:'2-digit'}) : 'en cours'}
                  </span>
                )}
                <span className="text-xs text-blue-600 font-medium">
                  {e.duration_minutes > 0 ? `${Math.floor(Math.round(e.duration_minutes)/60)}h${String(Math.round(e.duration_minutes)%60).padStart(2,'0')}` : '-'}
                </span>
                {canEditPricing && (
                  <span className="font-bold text-slate-900">
                    {e.duration_minutes > 0 ? `${((Math.round(e.duration_minutes) / 60) * timeEntries.hourly_rate).toFixed(2)} CHF` : '-'}
                  </span>
                )}
                {canEditPricing && workOrder.status !== 'completed' && (
                  <>
                    <button type="button" onClick={() => { setEditingTimeEntry(e); setEditDuration(String(e.duration_minutes)); }}
                      className="text-slate-400 hover:text-blue-600" data-testid={`edit-time-${i}`}><Edit className="w-3.5 h-3.5" /></button>
                    <button type="button" onClick={() => deleteTimeEntry(e.id)}
                      className="text-slate-400 hover:text-red-600" data-testid={`delete-time-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </>
                )}
              </div>
            </div>
          ))}
          <div className="flex justify-between items-center pt-2 border-t border-slate-200">
            <span className="text-xs text-slate-500">
              Total: {timeEntries.total_hours}h {canEditPricing ? `x ${timeEntries.hourly_rate} CHF/h (${timeEntries.work_type_label})` : ''}
            </span>
            {canEditPricing && (
              <span className={`font-bold ${allocatedHours && timeEntries.total_hours > parseFloat(allocatedHours) ? 'text-red-600' : 'text-blue-700'}`} data-testid="labour-total-cost">
                {timeEntries.total_cost_ht.toFixed(2)} CHF HT
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-4 border-2 border-dashed border-slate-200 rounded-lg">
          <p className="text-sm text-slate-400">Aucune heure pointee sur cette fiche</p>
        </div>
      )}

      {/* Edit time entry dialog */}
      {editingTimeEntry && (
        <Dialog open={!!editingTimeEntry} onOpenChange={() => setEditingTimeEntry(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Modifier les heures — {editingTimeEntry.employee_name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Duree (minutes)</Label>
                <Input type="number" min="0" value={editDuration} onChange={(e) => setEditDuration(e.target.value)} autoFocus data-testid="edit-duration-input" />
                <p className="text-xs text-slate-400 mt-1">
                  = {editDuration ? `${Math.floor(parseInt(editDuration)/60)}h${String(parseInt(editDuration)%60).padStart(2,'0')}` : '0h00'}
                  {' '}= {editDuration ? `${((parseInt(editDuration) / 60) * (timeEntries?.hourly_rate || 0)).toFixed(2)} CHF` : '0 CHF'}
                </p>
              </div>
              <Button className="w-full" onClick={saveTimeEntry} data-testid="save-time-entry-btn">Enregistrer</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};
